import { z } from 'zod';
import { courseLevelSchema, matchedOnSchema, safeText } from './common.js';
import { courseSummarySchema } from './course.js';
import { snippetSchema } from './search.js';
/**
 * The agentic search contract.
 *
 * One endpoint, two encodings. `Accept: application/json` returns {@link agentSearchResponseSchema}
 * whole; anything else streams the same object as it is assembled, event by event, because
 * twenty seconds of silence reads as a broken page rather than as a model thinking.
 *
 * Both encodings resolve to the same shape, and the SSE `done` event carries it verbatim. A
 * client that only implements the JSON path is therefore complete, and a client that implements
 * the streaming path never has to parse two different response formats.
 */
/** Longer than the catalog's 500 because a question is not a query. */
export const AGENT_QUERY_MAX_LENGTH = 1_000;
/**
 * Filters the *caller* pins, as opposed to the ones the model chooses.
 *
 * These are a ceiling, not a suggestion: they are intersected into every retrieval the agent
 * performs and the model is told about them but cannot widen them. A person who has ticked
 * "beginner" in the sidebar and then asks a question must not be shown advanced courses because
 * the model decided they were a better answer.
 */
export const agentFilterSchema = z.object({
    levels: z.array(courseLevelSchema).max(3).optional(),
    hasTranscripts: z.boolean().optional(),
    minHours: z.number().min(0).max(1_000).optional(),
    maxHours: z.number().min(0).max(1_000).optional(),
    authors: z.array(safeText(200)).max(20).optional(),
    skills: z.array(safeText(200)).max(20).optional(),
    knowledgeSuite: safeText(200).optional(),
});
/**
 * A prior turn.
 *
 * Only `user` and `assistant` — a client cannot inject a system message, and it cannot replay
 * tool results either. Tool output is re-derived from this request's own tools every time, which
 * is what keeps citation validation meaningful: a citation is checked against what *this* request
 * retrieved, and a forged history could otherwise supply the allowlist.
 */
export const agentTurnSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: safeText(4_000),
});
/**
 * Messages, not exchanges: six means three question-and-answer pairs.
 *
 * Enough for "the second one" and "cheaper alternatives" to resolve, which is what multi-turn is
 * for here, without spending the context window on the conversation rather than the retrieval.
 */
export const AGENT_MAX_HISTORY_TURNS = 6;
export const agentSearchRequestSchema = z.object({
    query: safeText(AGENT_QUERY_MAX_LENGTH),
    filters: agentFilterSchema.optional(),
    /**
     * Older turns beyond {@link AGENT_MAX_HISTORY_TURNS} are summarised rather than replayed. The
     * hard cap here is a body-size limit; the conversational cap is applied server-side.
     */
    history: z.array(agentTurnSchema).max(40).optional(),
});
/** Enough to name a lesson and deep-link into it. Never transcript text. */
export const lessonRefSchema = z.object({
    id: z.string(),
    lessonNo: z.number().int(),
    title: z.string().nullable(),
    moduleId: z.string(),
    moduleNo: z.number().int(),
    moduleTitle: z.string().nullable(),
});
/**
 * Why a cited result was retrieved.
 *
 * Hybrid retrieval is two arms fused, and which arm found a result is the difference between
 * "this course says SAA-C03" and "this course is about the same thing in different words". A
 * reader deciding whether to trust a recommendation wants that distinction, and a maintainer
 * debugging a bad answer needs it — so it is carried through rather than reconstructed.
 *
 * `null` on a citation means the result was **fetched by id, not searched for**: `get_course`,
 * `compare_courses` and `list_pathways` return rows the model asked for by name, and inventing a
 * rank for those would claim a retrieval that never happened.
 */
export const citationEvidenceSchema = z.object({
    matchedOn: matchedOnSchema,
    /** 1-based rank within the arm's candidate pool, or null when that arm did not find it. */
    vectorRank: z.number().int().positive().nullable(),
    lexicalRank: z.number().int().positive().nullable(),
    /**
     * Lesson citations only: the matching passage, with `highlights` marking the matched terms.
     *
     * Structured data, never markup. These are excerpts from 97 MB of third-party transcript, and
     * returning `<mark>` would make every one of them an injection vector no amount of escaping
     * downstream could undo (00-context §10.8).
     */
    snippet: snippetSchema.optional(),
    /** Lesson citations only: how many of the lesson's chunks matched. */
    matchedChunks: z.number().int().nonnegative().optional(),
});
/**
 * A numbered reference, resolved server-side.
 *
 * The model emits `[[course:<id>]]` and `[[lesson:<id>@<ms>]]`; those markers never reach a
 * client. Every one is checked against the tool results of the same request, dropped if it names
 * something no tool returned, and only then renumbered `[1]`, `[2]`, … — so a citation in the
 * answer text always has an entry here, and every entry always points at a real row.
 */
export const agentCitationSchema = z.discriminatedUnion('type', [
    z.object({
        n: z.number().int().positive(),
        type: z.literal('course'),
        course: courseSummarySchema,
        evidence: citationEvidenceSchema.nullable(),
    }),
    z.object({
        n: z.number().int().positive(),
        type: z.literal('lesson'),
        course: courseSummarySchema,
        lesson: lessonRefSchema,
        /**
         * Milliseconds into the **lesson**, which is exactly what `/courses/:slug/lessons/:id?t=`
         * takes once divided by a thousand.
         *
         * Not the chunk's own `start_ms`: segment timelines each restart at zero, so that figure
         * would send every citation to the top of its lesson. `search_lessons` resolves the
         * lesson-relative offset in SQL and the model never sees the other one (00-context §11).
         */
        startMs: z.number().int().nonnegative(),
        /** `HH:MM:SS` or `MM:SS`. Formatted once, server-side, so every surface agrees. */
        timestamp: z.string(),
        evidence: citationEvidenceSchema.nullable(),
    }),
]);
/** A suggestion's reason, capped server-side. One clause, not a paragraph. */
export const SUGGESTION_REASON_MAX_LENGTH = 160;
/**
 * A course offered as *adjacent* to the question rather than as an answer to it.
 *
 * This is deliberately not a citation, and the separation is the whole design. The first version
 * of "you might also like" did not exist as a concept: the model simply named a nearby course and
 * cited it, so it rendered as an ordinary numbered result card. Asked for "underwater basket
 * weaving" the answer said — correctly — that the catalog does not cover it, and then put two
 * critical-thinking courses on the page in the same visual position as a real recommendation.
 *
 * A card under the prose *reads* as an answer however the prose hedges. So a suggestion is a
 * different type, carries its own reason, is never numbered, never appears in `citations`, and is
 * never counted as a result — `citationCount` stays the measure of whether the catalog actually
 * answered, which is what the zero-result analytics depend on.
 *
 * Validated against this request's retrieval like every citation: a suggestion naming something
 * no tool returned is dropped before anyone sees it.
 */
export const agentSuggestionSchema = z.object({
    course: courseSummarySchema,
    /**
     * Why this is worth a look, in the model's own words.
     *
     * **Required, and a suggestion without one is dropped.** The section sits apart from the prose,
     * so a reader scanning it has only the card to go on — and an unexplained card is exactly the
     * thing a reader has to evaluate and reject. Making it structural means the rule cannot be
     * half-followed.
     */
    reason: z.string().min(1).max(SUGGESTION_REASON_MAX_LENGTH),
    evidence: citationEvidenceSchema.nullable(),
});
/** Three at most. Beyond that it is a second result list wearing a different hat. */
export const MAX_SUGGESTIONS = 3;
export const agentMetaSchema = z.object({
    iterations: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    tokensIn: z.number().int().nonnegative(),
    tokensOut: z.number().int().nonnegative(),
    latencyMs: z.number().int().nonnegative(),
    /** A budget was reached — iterations, tool calls, context or the wall clock. */
    truncated: z.boolean(),
    /**
     * The model did not produce the answer and plain hybrid search answered instead. `courses` is
     * the ranked result list and there are no citations. A broken inference endpoint degrades the
     * page; it does not break it.
     *
     * `answer` is empty when the model was unreachable, and a short server-written sentence when
     * the request was *refused* — Azure's safety classifier rejects some prompts outright, and a
     * refusal that renders identically to an outage is a page nobody can act on.
     */
    degraded: z.boolean(),
});
export const agentSearchResponseSchema = z.object({
    /** Prose with `[n]` markers. Plain text — never HTML, never markdown the client must trust. */
    answer: z.string(),
    citations: z.array(agentCitationSchema),
    /** Deduplicated, in the order first cited, for rendering cards beside the prose. */
    courses: z.array(courseSummarySchema),
    /**
     * Adjacent courses, rendered apart from the answer and never as part of it.
     *
     * Empty is a normal and frequent outcome: a question the catalog answers well needs no
     * alternatives, and a subject it does not cover at all has no adjacent anything. The model is
     * told that offering none is the right call more often than not.
     */
    suggestions: z.array(agentSuggestionSchema).max(MAX_SUGGESTIONS),
    meta: agentMetaSchema,
});
/**
 * The server-sent event vocabulary.
 *
 * Declared as schemas so the web client in prompt 12 parses them rather than trusting them, and
 * so the OpenAPI document can describe a streaming endpoint at all.
 */
export const agentStreamEventSchema = z.discriminatedUnion('event', [
    z.object({
        event: z.literal('status'),
        data: z.object({ phase: z.enum(['planning', 'searching', 'writing']) }),
    }),
    z.object({
        event: z.literal('tool'),
        data: z.object({
            name: z.string(),
            args: z.record(z.string(), z.unknown()),
            resultCount: z.number().int().nonnegative(),
            durationMs: z.number().int().nonnegative(),
            error: z.string().optional(),
        }),
    }),
    z.object({ event: z.literal('token'), data: z.object({ text: z.string() }) }),
    z.object({ event: z.literal('citation'), data: agentCitationSchema }),
    z.object({ event: z.literal('done'), data: agentSearchResponseSchema }),
    z.object({
        event: z.literal('error'),
        data: z.object({ code: z.string(), message: z.string() }),
    }),
]);
//# sourceMappingURL=agent.js.map